import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';

import serverVars from 'server-vars';
import PenIcon from 'dibs-icons/exports/legacy/Pen.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import Facebook from 'dibs-icons/exports/legacy/Facebook.ts';
import AnchorLink from 'dibs-icons/exports/legacy/AnchorLink.ts';
import Instagram from 'dibs-icons/exports/legacy/Instagram.ts';
import Twitter from 'dibs-icons/exports/legacy/Twitter.ts';
import WikipediaW from 'dibs-icons/exports/legacy/WikipediaW.ts';
import { useLoggedInUser } from 'dibs-buyer-layout/exports/useLoggedInUser.ts';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import CirclePlus from 'dibs-icons/exports/legacy/CirclePlus.ts';
import { trackEvent } from 'dibs-tracking';

import { DiscordTooltip } from '../../../components/DiscordTooltip.tsx';
import { profileLinks } from '../../../helpers/profileHelpers.tsx';
import { NftPublicProfileReportTooltip } from '../NftPublicProfileReport/NftPublicProfileReportTooltip.tsx';
import { NftPublicProfileAddressBadge } from '../NftPublicProfileAddressBadge/NftPublicProfileAddressBadge.tsx';
import { type NftPublicProfileInfo_viewer$key } from './__generated__/NftPublicProfileInfo_viewer.graphql.ts';

import styles from './NftPublicProfileInfo.scss';

type Props = {
    viewer: NftPublicProfileInfo_viewer$key | null | undefined;
    userHasMinterRole: boolean | null;
};

const messages = defineMessages({
    web: { id: 'nft.publicProfile.link.web', defaultMessage: 'Website' },
    instagram: { id: 'nft.publicProfile.link.instagram', defaultMessage: 'Instagram' },
    facebook: { id: 'nft.publicProfile.link.facebook', defaultMessage: 'Facebook' },
    twitter: { id: 'nft.publicProfile.link.twitter', defaultMessage: 'Twitter' },
    wikipedia: { id: 'nft.publicProfile.link.wikipedia', defaultMessage: 'Wikipedia' },
});

export const NftPublicProfileInfo: FC<Props> = ({ viewer: viewerRef }) => {
    const isMobile = serverVars.get('settings.isMobile');
    const { formatMessage } = useIntl();
    const viewer = useFragment(
        graphql`
            fragment NftPublicProfileInfo_viewer on Viewer {
                publicProfile(username: $username) {
                    userId
                    username
                    displayName
                    bio
                    webLink
                    discordLink
                    instagramLink
                    facebookLink
                    wikipediaLink
                    twitterLink
                    walletAddress {
                        address
                        ensDomain
                    }
                }
            }
        `,
        viewerRef
    );

    const {
        userId,
        username,
        displayName,
        bio,
        webLink,
        discordLink,
        instagramLink,
        facebookLink,
        wikipediaLink,
        twitterLink,
    } = viewer?.publicProfile ?? {};
    const { address: walletAddress, ensDomain } = viewer?.publicProfile?.walletAddress ?? {};

    const currentUserId = useLoggedInUser();

    const hasSocials =
        webLink || instagramLink || twitterLink || facebookLink || wikipediaLink || discordLink;

    const trackSocials = (social: string): void => {
        trackEvent({
            category: 'product',
            action: 'nft profile social media profile click',
            label: social,
            noninteraction: 0,
        });
    };

    return (
        <div className={styles.outerContainer}>
            <div className={styles.container}>
                {displayName && <h1 className={styles.nameHeading}>{displayName}</h1>}

                <div className={styles.nameWalletContainer}>
                    <HeadingLevel>
                        {Heading =>
                            displayName ? (
                                <Heading className={styles.username}>{username}</Heading>
                            ) : (
                                <h1 className={styles.username}>{username}</h1>
                            )
                        }
                    </HeadingLevel>
                    {walletAddress ? (
                        <NftPublicProfileAddressBadge
                            walletAddress={walletAddress}
                            ensDomain={ensDomain}
                        />
                    ) : null}
                </div>

                {!!hasSocials && (
                    <div className={styles.socials}>
                        {!!webLink && (
                            <Link
                                target="_blank"
                                href={webLink}
                                onClick={() => trackSocials('web')}
                            >
                                <AnchorLink
                                    className={styles.socialIcon}
                                    ariaLabel={formatMessage(messages.web)}
                                    role="img"
                                />
                            </Link>
                        )}
                        {!!instagramLink && (
                            <Link
                                target="_blank"
                                href={profileLinks.instagram(instagramLink)}
                                onClick={() => trackSocials('instagram')}
                            >
                                <Instagram
                                    className={styles.socialIcon}
                                    ariaLabel={formatMessage(messages.instagram)}
                                    role="img"
                                />
                            </Link>
                        )}
                        {!!twitterLink && (
                            <Link
                                target="_blank"
                                href={profileLinks.twitter(twitterLink)}
                                onClick={() => trackSocials('twitter')}
                            >
                                <Twitter
                                    className={styles.socialIcon}
                                    ariaLabel={formatMessage(messages.twitter)}
                                    role="img"
                                />
                            </Link>
                        )}
                        {!!facebookLink && (
                            <Link
                                target="_blank"
                                href={profileLinks.facebook(facebookLink)}
                                onClick={() => trackSocials('facebook')}
                            >
                                <Facebook
                                    className={styles.socialIcon}
                                    ariaLabel={formatMessage(messages.facebook)}
                                    role="img"
                                />
                            </Link>
                        )}
                        {!!wikipediaLink && (
                            <Link
                                target="_blank"
                                href={wikipediaLink}
                                onClick={() => trackSocials('wikipedia')}
                            >
                                <WikipediaW
                                    className={styles.socialIcon}
                                    ariaLabel={formatMessage(messages.wikipedia)}
                                    role="img"
                                />
                            </Link>
                        )}
                        {!!discordLink && (
                            <DiscordTooltip onOpen={() => trackSocials('discord')}>
                                {discordLink}
                            </DiscordTooltip>
                        )}
                        {!!username && <NftPublicProfileReportTooltip username={username} />}
                    </div>
                )}

                {!!bio && (
                    <div className={styles.bioContainer}>
                        <ExpandingAreaWithReadMore collapsedHeight={120} showGradient={false}>
                            <p className={styles.bioText}>{bio}</p>
                        </ExpandingAreaWithReadMore>
                    </div>
                )}

                {currentUserId === userId && (
                    <>
                        <Link
                            href="/profile-edit"
                            buyerLinkType="linkNoDecoration"
                            className={styles.editProfileLink}
                        >
                            <PenIcon className={styles.editIcon} />
                            {!isMobile && (
                                <FormattedMessage
                                    id="nft.publicProfile.editProfileCta"
                                    defaultMessage="Edit Profile"
                                />
                            )}
                        </Link>
                        {isMobile && (
                            <Link
                                href="/nft-create"
                                className={styles.floatingButton}
                                buyerLinkType="linkNoDecoration"
                            >
                                <CirclePlus className={styles.plusIcon} />
                                <FormattedMessage
                                    id="nft.profileLayout.createProfileCta"
                                    defaultMessage="Create NFT"
                                />
                            </Link>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
